import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import "../../../src/theme/variables.scss";
const comboBoxStyles = {
  sx: {
    "& .MuiFilledInput-root": {
      borderRadius: "4px",
      border: "1px solid var(--component-border)",
      background: "var(--background-color)",
      "&:before": { display: "none" },
      "&:after": { display: "none" },
      fontFamily: "var(--auro-font-family-default)",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 450,
      lineHeight: "16px",
      color: "var(--dark-blue-primary)",
      // Error state
      "&.Mui-error": {
        border: "2px solid var(--color-error)",
        borderColor: "var(--color-error)",
      },
    },
    "& .MuiFilledInput-root:hover": {
      background: "var(--background-color)",
      border: "2px solid var(--color-light-blue-primary)",
      //color: "inherit",
      // Error state hover
      "&.Mui-error": {
        borderColor: "var(--color-error) !important",
      },
    },
    "& .MuiFilledInput-root.Mui-focused": {
      background: "var(--background-color)",
      border: "2px solid var(--color-light-blue-primary)",
      color: "var(--dark-blue-primary)",
      // Error state focus
      "&.Mui-error": {
        borderColor: "var(--color-error) !important",
      },
    },
     "& .MuiInputLabel-root": {
      color: "var(--text-placeholder-color) !important",
      fontSize: "16px",
    },
    // Error helper text styling
    "& .MuiFormHelperText-root.Mui-error": {
      color: "var(--color-error)",
      fontSize: "var(--error-font-size)",
      fontWeight: 500,
      lineHeight: "1rem",
      margin: "var(--auro-size-50, 0.25rem) 0",
      fontFamily: "var(--auro-font-family-default)",
    },
    "& .MuiAutocomplete-option": {
      fontFamily: "var(--auro-font-family-default)",
      fontSize: "16px",
      color: "var(--dark-blue-primary)",
      padding: "8px 16px",
      cursor: "pointer",
      "&[aria-selected='true']": {
        backgroundColor: "var(--light-background-gray-color)",
        color: "var(--dark-blue-primary)",
      },
      "& .Mui-focused": {
        backgroundColor: "var(--light-background-gray-color)",
      },
    },
    // Clear indicator styling
    "& .MuiAutocomplete-clearIndicator": {
      color: "var(--component-border)",
      "&:hover": {
        color: "var(--color-error)",
      },
    },
    // Popup indicator styling
    "& .MuiAutocomplete-popupIndicator": {
      color: "var(--component-border)",
      "&:hover": {
        color: "var(--color-light-blue-primary)",
      },
    },
  },
};


type MaterialUIComboBoxProps = {
  name?: string;
  label?: string;
  id?: string;
  value?: any;
  onChange?: (event: any, value: any) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: object;
  variant?: "filled" | "standard" | "outlined";
  options: Array<string | { label: string; value: any }>;
  freeSolo?: boolean;
  className?: string;
  [key: string]: any;
};

const MaterialUIComboBox: React.FC<MaterialUIComboBoxProps> = ({
  name,
  label,
  id = "custom-combo-box",
  value,
  onChange,
  error,
  helperText,
  sx = {},
  variant = "filled",
  options,
  freeSolo = true,
  className,
  ...props
}) => {
  let formContext;
  try {
    formContext = useFormContext();
  } catch {
    formContext = undefined;
  }

  const mergedSx = { ...comboBoxStyles.sx, ...sx };

  const normalizedOptions = options.map(opt =>
    typeof opt === "string" ? { label: opt, value: opt } : opt
  );

  if (formContext && formContext.control && name) {
    return (
      <Controller
        name={name}
        control={formContext.control}
        render={({
          field: { onChange: rhfOnChange, value: rhfValue, ref },
          fieldState: { error: rhfError }
        }) => (
          <Autocomplete
            freeSolo={freeSolo}
            options={normalizedOptions}
            getOptionLabel={option =>
              typeof option === "string" ? option : option.label
            }
            value={
              normalizedOptions.find(opt =>
                typeof opt === "string"
                  ? opt === rhfValue
                  : opt.value === rhfValue || opt.label === rhfValue
              ) || (freeSolo ? rhfValue || "" : null)
            }
            onChange={(_, newValue) => {
              let val =
                typeof newValue === "string"
                  ? newValue
                  : newValue?.value ?? newValue?.label ?? "";
              rhfOnChange(val);
            }}
            inputValue={rhfValue ?? ""}
            onInputChange={(_, newInputValue) => {
              rhfOnChange(newInputValue);
            }}
            renderInput={params => (
              <TextField
                {...params}
                inputRef={ref}
                id={id}
                label={label}
                error={!!rhfError || !!error}
                helperText={rhfError ? rhfError.message : helperText}
                fullWidth
                variant={variant}
                sx={mergedSx}
              />
            )}
            className={className}
            {...props}
          />
        )}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo={freeSolo}
      options={normalizedOptions}
      getOptionLabel={option =>
        typeof option === "string" ? option : option.label
      }
      disableClearable={true}
      value={
        normalizedOptions.find(opt =>
          typeof opt === "string"
            ? opt === value
            : opt.value === value || opt.label === value
        ) || (freeSolo ? value || "" : null)
      }
      onChange={(_, newValue) => {
        let val =
          typeof newValue === "string"
            ? newValue
            : newValue?.value ?? newValue?.label ?? "";
        onChange?.(_, val);
      }}
      inputValue={value ?? ""}
      onInputChange={(_, newInputValue) => {
        onChange?.(_, newInputValue);
      }}
      renderInput={params => (
        <TextField
          {...params}
          id={id}
          label={label}
          error={!!error}
          helperText={helperText}
          fullWidth
          variant={variant}
          sx={mergedSx}
        />
      )}
       className={className}
      {...props}
    />
  );
};

export default MaterialUIComboBox;
